import type {
  ChapterContractSufficiency,
  ChapterExpansionPlan,
  ChapterPlan,
  ExpansionDeficitStrategy,
  ExpansionOutputMetadata,
  ExpansionPassResult,
  ExpansionPassStatus,
  SceneExpansionAllocation,
  SceneExpansionInsertion,
} from '../types';

export const ALLOWED_EXPANSION_METHODS = [
  'meaningful dialogue',
  'disagreement or relationship tension',
  'new obstacle',
  'failed attempt that changes available options',
  'decision with consequences',
  'revelation',
  'moral or practical dilemma',
  'character reaction that changes behavior',
  'escalation of danger',
  'complication',
  'stronger setup/payoff',
  'discovery with immediate consequences',
] as const;

export const FORBIDDEN_PADDING_METHODS = [
  'repeated environment description',
  'equipment inventory',
  'repeated mechanics',
  'routine labor',
  'redundant internal monologue',
  'travel transcription',
  'repeated action loops',
  'restating information',
  'adding adjectives without new meaning',
] as const;

export interface ChapterWordTargets {
  targetWordCount: number;
  minimumAcceptedWords: number;
  targetWordRangeMin: number;
  targetWordRangeMax: number;
  majorDeficitThreshold: number;
  minorDeficitThreshold: number;
  incompleteDraftThreshold: number;
}

export function countChapterWords(text: string): number {
  return (text || '').split(/\s+/).filter(Boolean).length;
}

export function resolveChapterWordTargets(chapterLength?: string): ChapterWordTargets {
  const targetWordCount =
    chapterLength === 'MICRO' ? 1000 : chapterLength === 'EXTENDED' ? 5000 : 2500;
  const minimumAcceptedWords = Math.round(targetWordCount * 0.8);
  const targetWordRangeMin = Math.round(targetWordCount * 0.84);
  const targetWordRangeMax = targetWordCount;
  const majorDeficitThreshold = Math.round(minimumAcceptedWords * 0.65);
  const minorDeficitThreshold = Math.round(minimumAcceptedWords * 0.8);
  const incompleteDraftThreshold = Math.round(minimumAcceptedWords * 0.6);

  return {
    targetWordCount,
    minimumAcceptedWords,
    targetWordRangeMin,
    targetWordRangeMax,
    majorDeficitThreshold,
    minorDeficitThreshold,
    incompleteDraftThreshold,
  };
}

export function classifyDeficitStrategy(
  currentWordCount: number,
  targets: ChapterWordTargets = resolveChapterWordTargets(),
): ExpansionDeficitStrategy {
  if (currentWordCount < targets.majorDeficitThreshold) return 'MAJOR';
  if (currentWordCount >= targets.minorDeficitThreshold) return 'MINOR';
  return currentWordCount < targets.majorDeficitThreshold ? 'MAJOR' : 'MINOR';
}

export function isIncompleteInitialDraft(
  currentWordCount: number,
  targets: ChapterWordTargets = resolveChapterWordTargets(),
): boolean {
  return currentWordCount < targets.incompleteDraftThreshold;
}

export function computeMinimumUsefulGrowth(requiredAdditionalWords: number): number {
  return Math.max(250, Math.floor(requiredAdditionalWords * 0.4));
}

export interface ParsedScene {
  sceneId: string;
  text: string;
}

export function splitChapterIntoScenes(content: string): ParsedScene[] {
  const trimmed = (content || '').trim();
  if (!trimmed) return [{ sceneId: 'scene-1', text: '' }];

  const parts = trimmed.split(/\n\s*\*\*\*\s*\n/).map((s) => s.trim()).filter(Boolean);
  if (parts.length > 1) {
    return parts.map((text, index) => ({ sceneId: `scene-${index + 1}`, text }));
  }

  const paragraphs = trimmed.split(/\n\s*\n+/).map((p) => p.trim()).filter(Boolean);
  if (paragraphs.length >= 4) {
    const chunkSize = Math.ceil(paragraphs.length / 4);
    const scenes: ParsedScene[] = [];
    for (let i = 0; i < paragraphs.length; i += chunkSize) {
      scenes.push({
        sceneId: `scene-${scenes.length + 1}`,
        text: paragraphs.slice(i, i + chunkSize).join('\n\n'),
      });
    }
    return scenes;
  }

  return [{ sceneId: 'scene-1', text: trimmed }];
}

function lastParagraphAnchor(sceneText: string): string {
  const paragraphs = sceneText.split(/\n\s*\n+/).map((p) => p.trim()).filter(Boolean);
  const anchor = paragraphs[paragraphs.length - 1] || sceneText.trim();
  return anchor.slice(0, Math.min(120, anchor.length));
}

function scoreSceneExpansionOpportunity(sceneText: string, plan?: ChapterPlan): number {
  const words = countChapterWords(sceneText);
  const beatHits = (plan?.requiredBeats || []).filter((beat) => {
    const tokens = beat.toLowerCase().split(/\W+/).filter((t) => t.length > 4);
    const lower = sceneText.toLowerCase();
    return tokens.some((t) => lower.includes(t));
  }).length;

  const hasDialogue = /["“‘]/.test(sceneText);
  const hasConflict = /\b(but|however|refused|argued|couldn't|failed|threat|danger)\b/i.test(sceneText);
  const thinSceneBoost = words < 180 ? 2 : words < 300 ? 1.5 : 1;
  const underdevelopedBoost = beatHits === 0 ? 1.4 : 1;
  const dialogueBoost = hasDialogue ? 1.1 : 1.25;
  const conflictBoost = hasConflict ? 1.15 : 1.3;

  return Math.max(0.5, thinSceneBoost * underdevelopedBoost * dialogueBoost * conflictBoost);
}

function deriveDramaticPurpose(scene: ParsedScene, plan: ChapterPlan | undefined, index: number): string {
  const beat = plan?.requiredBeats?.[index] || plan?.requiredBeats?.[plan.requiredBeats.length - 1];
  if (beat) return `Deepen roadmap beat: ${beat}`;
  if (index === 0) return 'Escalate opening pressure and clarify what the POV character wants.';
  if (index >= 2) return 'Add complication, decision, or consequence before the chapter turn.';
  return 'Add interpersonal tension, obstacle, or revelation that changes available options.';
}

export function allocateSceneExpansions(
  scenes: ParsedScene[],
  plan: ChapterPlan | undefined,
  requiredAdditionalWords: number,
): SceneExpansionAllocation[] {
  if (scenes.length === 0 || requiredAdditionalWords <= 0) return [];

  const scored = scenes.map((scene, index) => ({
    scene,
    index,
    weight: scoreSceneExpansionOpportunity(scene.text, plan),
  }));
  const totalWeight = scored.reduce((sum, item) => sum + item.weight, 0) || 1;

  return scored.map(({ scene, index, weight }) => ({
    sceneId: scene.sceneId,
    currentWords: countChapterWords(scene.text),
    targetAdditionalWords: Math.max(
      80,
      Math.round((requiredAdditionalWords * weight) / totalWeight),
    ),
    dramaticPurpose: deriveDramaticPurpose(scene, plan, index),
    allowedExpansionMethods: [...ALLOWED_EXPANSION_METHODS],
    forbiddenPaddingMethods: [...FORBIDDEN_PADDING_METHODS],
    insertionAnchor: lastParagraphAnchor(scene.text),
  }));
}

export function isChapterExpansionPlan(value: unknown): value is ChapterExpansionPlan {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as ChapterExpansionPlan;
  return (
    (candidate.deficitStrategy === 'MAJOR' || candidate.deficitStrategy === 'MINOR') &&
    typeof candidate.currentWordCount === 'number' &&
    typeof candidate.minimumAcceptedWords === 'number' &&
    typeof candidate.minimumUsefulGrowth === 'number' &&
    typeof candidate.requiredAdditionalWords === 'number' &&
    Array.isArray(candidate.sceneExpansions)
  );
}

export interface MajorExpansionDispatchDecision {
  expansionPlan: ChapterExpansionPlan;
  dispatchStrategy: ExpansionDeficitStrategy;
  authoritativeStrategy: ExpansionDeficitStrategy;
  planStrategyMismatch: boolean;
  ignoredInvalidInput: boolean;
}

export function resolveExpansionPlanInput(
  expansionPlanInput: unknown,
  draftContent: string,
  plan?: ChapterPlan,
  chapterLength?: string,
): { expansionPlan: ChapterExpansionPlan; ignoredInvalidInput: boolean } {
  if (isChapterExpansionPlan(expansionPlanInput)) {
    return { expansionPlan: expansionPlanInput, ignoredInvalidInput: false };
  }
  return {
    expansionPlan: buildChapterExpansionPlan(draftContent, plan, chapterLength),
    ignoredInvalidInput: expansionPlanInput != null,
  };
}

/** Authoritative MAJOR/MINOR dispatch — never trust a non-plan object or stale plan strategy. */
export function resolveMajorExpansionDispatch(
  expansionPlanInput: unknown,
  draftContent: string,
  plan?: ChapterPlan,
  chapterLength?: string,
): MajorExpansionDispatchDecision {
  const { expansionPlan: resolvedPlan, ignoredInvalidInput } = resolveExpansionPlanInput(
    expansionPlanInput,
    draftContent,
    plan,
    chapterLength,
  );
  const targets = resolveChapterWordTargets(chapterLength);
  const authoritativeStrategy = classifyDeficitStrategy(countChapterWords(draftContent), targets);
  const planStrategyMismatch = resolvedPlan.deficitStrategy !== authoritativeStrategy;
  const expansionPlan = planStrategyMismatch
    ? buildChapterExpansionPlan(draftContent, plan, chapterLength)
    : resolvedPlan;

  return {
    expansionPlan,
    dispatchStrategy: authoritativeStrategy,
    authoritativeStrategy,
    planStrategyMismatch,
    ignoredInvalidInput,
  };
}

export function buildChapterExpansionPlan(
  draftContent: string,
  plan: ChapterPlan | undefined,
  chapterLength?: string,
): ChapterExpansionPlan {
  const targets = resolveChapterWordTargets(chapterLength);
  const currentWordCount = countChapterWords(draftContent);
  const requiredAdditionalWords = Math.max(0, targets.minimumAcceptedWords - currentWordCount);
  const deficitStrategy = classifyDeficitStrategy(currentWordCount, targets);
  const minimumUsefulGrowth = computeMinimumUsefulGrowth(requiredAdditionalWords);
  const scenes = splitChapterIntoScenes(draftContent);

  return {
    currentWordCount,
    targetWordCount: targets.targetWordCount,
    minimumAcceptedWords: targets.minimumAcceptedWords,
    targetWordRangeMin: targets.targetWordRangeMin,
    targetWordRangeMax: targets.targetWordRangeMax,
    requiredAdditionalWords,
    deficitStrategy,
    minimumUsefulGrowth,
    sceneExpansions: allocateSceneExpansions(scenes, plan, requiredAdditionalWords),
  };
}

const CONFLICT_MARKERS = /\b(conflict|oppose|threat|fight|argue|refuse|block|danger|attack|betray|chase|escape)\b/i;
const DECISION_MARKERS = /\b(decide|choose|commit|agree|refuse|plan|strategy|must|will)\b/i;
const CONSEQUENCE_MARKERS = /\b(consequence|result|cost|lose|gain|because|therefore|after|changed)\b/i;
const DILEMMA_MARKERS = /\b(dilemma|torn|whether|doubt|fear|want|need|trust)\b/i;
const ENDING_MARKERS = /\b(discover|realize|reveal|end|finally|underground|new danger|changed)\b/i;

export function assessChapterContractSufficiency(plan?: ChapterPlan): ChapterContractSufficiency {
  const beats = plan?.requiredBeats || [];
  const summary = plan?.summary || '';
  const corpus = [summary, ...beats].join('\n');
  const sceneTurnCount = beats.length;
  const hasConflict = CONFLICT_MARKERS.test(corpus);
  const hasDecision = DECISION_MARKERS.test(corpus);
  const hasConsequence = CONSEQUENCE_MARKERS.test(corpus);
  const hasInteractionOrDilemma = DILEMMA_MARKERS.test(corpus) || /dialogue|relationship|argue/i.test(corpus);
  const hasEndingChange = ENDING_MARKERS.test(corpus) || beats.length >= 2;

  const issues: string[] = [];
  if (sceneTurnCount < 3) issues.push('Fewer than 3 meaningful scene turns/beats.');
  if (!hasConflict) issues.push('No clear conflict in chapter contract.');
  if (!hasDecision) issues.push('No explicit decision point in chapter contract.');
  if (!hasConsequence) issues.push('No stated consequence in chapter contract.');
  if (!hasInteractionOrDilemma) issues.push('No interaction or internal dilemma specified.');
  if (!hasEndingChange) issues.push('No ending change or discovery specified.');

  const suggestedBeats: string[] = [];
  if (sceneTurnCount < 3) {
    suggestedBeats.push(
      '[REQUIRED OUTCOME]: A complication forces the POV character to revise their immediate plan.',
      '[REQUIRED OUTCOME]: A decision with visible cost changes what the characters can do next.',
      '[REQUIRED OUTCOME]: A discovery at chapter end alters stakes or available options.',
    );
  } else {
    if (!hasConflict) suggestedBeats.push('[REQUIRED OUTCOME]: Introduce active opposition or obstacle to the POV goal.');
    if (!hasDecision) suggestedBeats.push('[REQUIRED OUTCOME]: Force an explicit choice with consequences.');
    if (!hasConsequence) suggestedBeats.push('[REQUIRED OUTCOME]: Show the cost or payoff of the prior decision.');
  }

  const sufficient =
    sceneTurnCount >= 3 &&
    hasConflict &&
    hasDecision &&
    hasConsequence &&
    hasInteractionOrDilemma &&
    hasEndingChange;

  return {
    sufficient,
    sceneTurnCount,
    hasConflict,
    hasDecision,
    hasConsequence,
    hasInteractionOrDilemma,
    hasEndingChange,
    issues,
    suggestedBeats,
  };
}

export function enrichChapterPlanForLength(plan: ChapterPlan): ChapterPlan {
  const assessment = assessChapterContractSufficiency(plan);
  if (assessment.sufficient) return plan;

  return {
    ...plan,
    requiredBeats: [...(plan.requiredBeats || []), ...assessment.suggestedBeats],
    continuityNotes: [
      ...(plan.continuityNotes || []),
      'Contract enriched for full-length dramatization before Scribe generation.',
    ],
  };
}

export function applySceneInsertions(
  scenes: ParsedScene[],
  insertions: SceneExpansionInsertion[],
): string {
  const sceneMap = new Map(scenes.map((scene) => [scene.sceneId, scene.text]));

  for (const insertion of insertions) {
    const existing = sceneMap.get(insertion.sceneId) ?? '';
    const anchor = insertion.afterAnchor?.trim();
    const passage = insertion.passage?.trim();
    if (!passage) continue;

    if (anchor && existing.includes(anchor)) {
      const insertAt = existing.indexOf(anchor) + anchor.length;
      sceneMap.set(
        insertion.sceneId,
        `${existing.slice(0, insertAt)}\n\n${passage}${existing.slice(insertAt)}`,
      );
    } else {
      sceneMap.set(insertion.sceneId, `${existing}\n\n${passage}`.trim());
    }
  }

  const merged = scenes.map((scene) => sceneMap.get(scene.sceneId) ?? scene.text);
  return merged.length > 1 ? merged.join('\n\n***\n\n') : merged[0] || '';
}

export function detectOutputTruncation(
  metadata: Pick<ExpansionOutputMetadata, 'finishReason' | 'outputWords' | 'requestedTargetWords'>,
): boolean {
  if (metadata.finishReason === 'MAX_TOKENS') return true;
  if (metadata.outputWords > 0 && metadata.outputWords < metadata.requestedTargetWords * 0.35) {
    return true;
  }
  return false;
}

export function buildExpansionOutputMetadata(params: {
  model?: string;
  requestedTargetWords: number;
  outputWords: number;
  finishReason?: string;
  outputTokenCount?: number;
}): ExpansionOutputMetadata {
  const metadata: ExpansionOutputMetadata = {
    requestedTargetWords: params.requestedTargetWords,
    outputWords: params.outputWords,
    finishReason: params.finishReason,
    outputTokenCount: params.outputTokenCount,
    truncated: false,
    model: params.model,
  };
  metadata.truncated = detectOutputTruncation(metadata);
  return metadata;
}

export function evaluateExpansionPassResult(params: {
  previousWordCount: number;
  newWordCount: number;
  minimumUsefulGrowth: number;
  metadata?: ExpansionOutputMetadata;
  continuityPasses?: boolean;
  momentumPasses?: boolean;
  paddingViolation?: boolean;
}): ExpansionPassResult {
  const addedWords = params.newWordCount - params.previousWordCount;
  let status: ExpansionPassStatus = 'SUCCESS';
  let reason: string | undefined;

  if (params.metadata?.truncated) {
    status = 'TRUNCATED';
    reason = 'Model output appears truncated before reaching the requested expansion length.';
  } else if (addedWords < params.minimumUsefulGrowth || params.newWordCount <= params.previousWordCount) {
    status = 'EXPANSION_INSUFFICIENT_GROWTH';
    reason = `Added only ${addedWords} words; need at least ${params.minimumUsefulGrowth}.`;
  } else if (params.paddingViolation) {
    status = 'FAILED';
    reason = 'Expansion introduced padding violations.';
  } else if (params.continuityPasses === false || params.momentumPasses === false) {
    status = 'FAILED';
    reason = 'Expansion failed continuity or momentum validation.';
  }

  return {
    status,
    addedWords,
    previousWordCount: params.previousWordCount,
    newWordCount: params.newWordCount,
    minimumUsefulGrowth: params.minimumUsefulGrowth,
    metadata: params.metadata,
    reason,
  };
}

export interface PostExpansionDraftState<T extends { content?: string }> {
  draft: T;
  wordCount: number;
  expansionFailed: boolean;
  keptOriginalDraft: boolean;
}

/** Mirrors App.tsx post-expansion draft selection: keep the best valid draft. */
export function resolvePostExpansionDraftState<T extends { content?: string }>(
  preExpansionDraft: T,
  expandedDraft: T,
  passResult: ExpansionPassResult,
): PostExpansionDraftState<T> {
  const preWordCount = passResult.previousWordCount;

  if (passResult.status === 'SUCCESS') {
    return {
      draft: expandedDraft,
      wordCount: passResult.newWordCount,
      expansionFailed: false,
      keptOriginalDraft: false,
    };
  }

  if (passResult.newWordCount > preWordCount) {
    return {
      draft: expandedDraft,
      wordCount: passResult.newWordCount,
      expansionFailed: true,
      keptOriginalDraft: false,
    };
  }

  return {
    draft: preExpansionDraft,
    wordCount: preWordCount,
    expansionFailed: true,
    keptOriginalDraft: true,
  };
}

export function shouldBlockPipelineAfterExpansionFailure(
  currentWordCount: number,
  minimumAcceptedWords: number,
  expansionFailed: boolean,
  expansionAttemptCount: number,
): boolean {
  return currentWordCount < minimumAcceptedWords && (expansionFailed || expansionAttemptCount > 0);
}

/** Replace chapter by id so persistence never creates duplicate chapter records. */
export function upsertChapterById<T extends { id: number }>(chapters: T[], chapter: T): T[] {
  const without = chapters.filter((c) => c.id !== chapter.id);
  return [...without, chapter].sort((a, b) => a.id - b.id);
}

export function formatExpansionProgressMessage(result: ExpansionPassResult): string {
  if (result.status === 'SUCCESS') {
    return `Expansion pass added ${result.addedWords} words.`;
  }
  if (result.status === 'EXPANSION_INSUFFICIENT_GROWTH') {
    if (result.addedWords < 0) {
      return `Expansion shrank the draft by ${Math.abs(result.addedWords)} words.`;
    }
    if (result.addedWords === 0) {
      return 'Expansion produced no additional words.';
    }
    return `Expansion added only ${result.addedWords} words; at least ${result.minimumUsefulGrowth} were required.`;
  }
  if (result.status === 'TRUNCATED') {
    return `Expansion output was truncated (+${result.addedWords} words). Retrying with a different strategy.`;
  }
  return result.reason || 'Expansion pass failed.';
}

export function buildWordCountTargetPrompt(targets: ChapterWordTargets): string {
  return `
CHAPTER LENGTH TARGETS:
- Practical target range: ${targets.targetWordRangeMin}–${targets.targetWordRangeMax} words
- Hard minimum acceptable: ${targets.minimumAcceptedWords} words
- Initial draft should aim near ${targets.targetWordRangeMin}–${targets.targetWordRangeMax} words through dramatized scenes — not a short skeleton requiring many repair passes.
`.trim();
}

export function buildExpansionPlanPrompt(plan: ChapterExpansionPlan): string {
  const sceneLines = plan.sceneExpansions
    .map(
      (scene) =>
        `- ${scene.sceneId}: add ~${scene.targetAdditionalWords} words — ${scene.dramaticPurpose}. Anchor after: "${scene.insertionAnchor || 'scene end'}".`,
    )
    .join('\n');

  return `
EXPANSION DEFICIT PLAN:
- Current words: ${plan.currentWordCount}
- Target range: ${plan.targetWordRangeMin}–${plan.targetWordRangeMax}
- Hard minimum: ${plan.minimumAcceptedWords}
- Required additional words: ${plan.requiredAdditionalWords}
- Minimum useful growth this pass: ${plan.minimumUsefulGrowth}
- Strategy: ${plan.deficitStrategy}

SCENE ALLOCATIONS (distribute dramatic substance — not padding):
${sceneLines}

ALLOWED EXPANSION METHODS: ${ALLOWED_EXPANSION_METHODS.join('; ')}
FORBIDDEN PADDING METHODS: ${FORBIDDEN_PADDING_METHODS.join('; ')}
Each inserted passage must state its narrative function.
`.trim();
}

export function passageUsesAllowedExpansionMethods(passage: string): boolean {
  const dialogue = /["“‘]/.test(passage);
  const conflict = /\b(argue|argued|refused|couldn't|failed|threat|danger|obstacle|complication|hated|rejected)\b/i.test(passage);
  const decision = /\b(decided|chose|agreed|had to|must|decide|together)\b/i.test(passage);
  const revelation = /\b(realized|discovered|understood|learned|revealed)\b/i.test(passage);
  const hasStoryMotion = dialogue || conflict || decision || revelation;
  return hasStoryMotion && !passageLooksLikePadding(passage);
}

export function passageLooksLikePadding(passage: string): boolean {
  const inventoryHits = (passage.match(/\b(coil|bolt|cartridge|inventory|equipment|toolbox|harness|spare bolts|water bladders)\b/gi) || []).length;
  const inventory = inventoryHits >= 2 || /\b(checked each bolt|equipment catalogs|toolbox)\b/i.test(passage);
  const travel = /\b(walked|traveled|miles|hours later|continued across)\b/i.test(passage);
  const repeatedSensory = (passage.match(/\b(sand|dust|wind|static|grit)\b/gi) || []).length >= 4;
  return inventory || (travel && !/["“‘]/.test(passage)) || repeatedSensory;
}
